// src/pages/UserProfile/ui/UserTrainingPlans/UserTrainingPlansIsland.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, ChevronDown, Filter } from 'lucide-react';
import './UserTrainingPlansIsland.css';
import Pagination from '../../../components/Pagination/Pagination';
import { formatNice } from '../../../utils/formatNice';
import { getIndefiniteArticle } from '@teleprompt/shared/utils/getIndefiniteArticle';
import TrainingCard from '../../../components/TrainingCard/TrainingCard';
import Dropdown from '../../../components/Dropdown/Dropdown';
// adjust relative path if needed

export default function UserTrainingPlansIsland({
  items = [],                 // flat list from API
  programProgress = {},       // { [programName]: { completed, total } }
  selectedProgram,
  setSelectedProgram,
  availableIndustries = [],
  selectedIndustry,
  setSelectedIndustry,
  dateRange,
  setDateRange,
  fmt,                        // date formatter
  onPlanAction,               // (plan) => void
  onOpenSchedule,
  onEmptyAction,
  page,
  pages,
  onPageChange,
  loading = false,
  canUseTrainingPlans = true,
}) {
  // group by program (already Title Cased by server)

  
  
  const grouped = useMemo(() => {
    const map = new Map();
    for (const plan of items) {
      const key = plan.program || 'General';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(plan);
    }
    return Array.from(map.entries()); // [ [programName, plans[]], ... ]
  }, [items]);


  // accordion open/close state per program
  const [openMap, setOpenMap] = useState(() =>
    Object.fromEntries(grouped.map(([prog]) => [prog, false])) // default all closed
  );
  // filter dropdown state
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      const clickedInsideFilter = filterRef.current?.contains(event.target);
      const clickedInsideDropdown = event.target.closest(".pm-dd-portal"); // 👈 NEW
    
      if (!clickedInsideFilter && !clickedInsideDropdown) {
        setShowFilters(false);
      }
    }
    

    if (showFilters) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilters]);
  // re-sync openMap if groups change (keeps previous state where possible)
  React.useEffect(() => {
    setOpenMap(prev => {
      const next = { ...prev };
      for (const [prog] of grouped) {
        if (!(prog in next)) next[prog] = true;
      }
      // optionally remove stale keys:
      Object.keys(next).forEach(k => {
        if (!grouped.find(([p]) => p === k)) delete next[k];
      });
      return next;
    });
  }, [grouped]);

  const toggle = useCallback(
    (programName) => setOpenMap(m => ({ ...m, [programName]: !m[programName] })),
    []
  );

  const now = new Date();

  function filterByDate(plans, dateRange) {
    if (dateRange === "all") return plans;

    return plans.filter(plan => {
      const created = new Date(plan.createdAt);

      if (dateRange === "today") {
        return created.toDateString() === now.toDateString();
      }

      if (dateRange === "7") {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(now.getDate() - 7);
        return created >= sevenDaysAgo;
      }

      if (dateRange === "month") {
        return (
          created.getMonth() === now.getMonth() &&
          created.getFullYear() === now.getFullYear()
        );
      }

      return true;
    });
  }


  return (
    <section className="dashboard-island">
      <header className="profile-island-head">
        <div className="profile-island-title">
          <BookOpen size={28} /> <span>Training Sessions</span>
        </div>

        <div className="filter-wrapper" ref={filterRef}>
          <button
            className="filter-btn"
            onClick={() => setShowFilters(s => !s)}
            aria-label="Filter training sessions"
          >
           Filter <Filter size={18} className="filter-icon" />
          </button>

          {showFilters && (
            <div className="filter-dropdown">
              <div className="filter-group">
                <h4>Programs</h4>
                <Dropdown
  options={[
    { value: "", label: "All Programs" },
    ...grouped.map(([prog]) => ({
      value: prog,
      label: formatNice(prog),
    }))
  ]}
  value={selectedProgram}
  onChange={(val) => { setSelectedProgram(val); onPageChange(1); }}
  placeholder="All Programs"
/>

              </div>

              {availableIndustries.length > 0 && (
                <div className="filter-group">
                  <h4>Industries</h4>
                  <Dropdown
    options={[
      { value: "", label: "All Industries" },
      ...availableIndustries.map(ind => ({
        value: ind,
        label: ind,
      }))
    ]}
    value={selectedIndustry}
    onChange={(val) => { setSelectedIndustry(val); onPageChange(1); }}
    placeholder="All Industries"
  />
                </div>
              )}

<div className="filter-group">
  <h4>Date Range</h4>
  <Dropdown
    options={[
      { value: "today", label: "Today" },
      { value: "7", label: "Last 7 days" },
      { value: "month", label: "This month" },
      { value: "all", label: "All time" },
    ]}
    value={dateRange}
    onChange={(val) => { setDateRange(val); onPageChange(1); }}
    placeholder="Select date range"
  />
</div>
            </div>
          )}
        </div>
      </header>



      {/* Groups */}
      <div className="tp-accordion">
        {loading && <div className="empty-state" style={{ marginTop: 8 }}>Loading…</div>}

        {!loading && grouped.length === 0 && (
          <div className="empty-state training-empty-state">
            <p className="empty-state-text training-empty-title">No training sessions yet</p>
            <p className="training-empty-copy">
              Complete a practice conversation to get started.
            </p>
            <button className="primary-button" onClick={onEmptyAction} type="button">
              Create Conversation
            </button>
          </div>
        )}

        {!loading && grouped.map(([programName, plans]) => {
          if (selectedProgram && programName !== selectedProgram) return null;
       
          const filteredPlans = filterByDate(plans, dateRange);
          if (filteredPlans.length === 0) return null;
          const summary = programProgress[programName] || { completed: 0, total: plans.length };
          const completed = summary.completed ?? plans.filter(p => p.status === 'Completed').length;
          const total = summary.total ?? plans.length;
          const isOpen = !!openMap[programName];
          const userRole = String(filteredPlans[0]?.meetingData?.userRole || '').trim();

          return (
            <div key={programName} className="tp-item">
              <button
                className="tp-head"
                onClick={() => toggle(programName)}
                aria-expanded={isOpen}
                aria-controls={`tp-panel-${programName}`}

              >
                <div className="tp-head-left">
                  <div className="tp-title-row">
                    <div className="tp-title">{formatNice(programName)}</div>
                    {userRole && (
                      <>
                        <span className="tp-role-connector">
                          as {getIndefiniteArticle(userRole)}
                        </span>
                        <span className="tp-role-pill" title={formatNice(userRole)}>
                          {formatNice(userRole)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="tp-head-right">
                  <div
                    className="tp-progress-pill"
                    aria-label={`${completed} of ${total} sessions`}
                    title={`${completed} of ${total} sessions`}
                  >
                    <span className="tp-progress-dot" aria-hidden="true" />
                    <span className="tp-progress-text">
                      {completed} / {total}
                    </span>
                  </div>

                  <ChevronDown
                    size={20}
                    className={`tp-chevron ${isOpen ? 'open' : ''}`}
                    aria-hidden="true"
                  />
                </div>



              </button>
              {/* 💡 Progress bar goes immediately under the header */}
              {(() => {
                const pct = total ? Math.round((completed / total) * 100) : 0;
                return (
                  <div className="tp-progress-bar" role="progressbar"
                    aria-valuemin={0} aria-valuemax={total || 1} aria-valuenow={completed}>
                    {/* Use plain width inline — more reliable than a CSS var */}
                    <span className="tp-progress-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                );
              })()}
              {isOpen && (
                <div id={`tp-panel-${programName}`} className="card-list">
                  {plans.map((plan) => {
                    const status = plan.status;
                    const created = fmt(plan.createdAt);
                    const duration = plan.meetingData?.duration
                      ? `${plan.meetingData.duration} min`
                      : "—";

                    return (
                      <TrainingCard
                        key={plan.id}
                        plan={plan}
                        status={status}
                        created={created}
                        duration={duration}
                        onOpenSchedule={onOpenSchedule}
                        onPlanAction={onPlanAction}
                        canUseTrainingPlans={canUseTrainingPlans}
                      />
                    );
                  })}
                </div>
              )}

            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="userprofile-pagination">
        <Pagination page={page} pages={pages} onChange={onPageChange} />
      </div>
    </section>
  );
}
